/**
 * Contact section. Plain contact details + a CTA that scroll-anchors back to
 * the hero RFQ form (#rfq) instead of duplicating the form here. Ends with the
 * muted release label (`v<version>`), the page's one visible version marker.
 */
import type { ReactNode } from "react";
import { SectionLabel } from "../layout/SectionLabel";
import { ObfuscatedPhone } from "../ObfuscatedPhone";

type ContactRow = {
  label: string;
  value?: string;
  href?: string;
  node?: ReactNode;
};

const CONTACTS: ContactRow[] = [
  { label: "Email", value: "sales@anyangle.co", href: "mailto:sales@anyangle.co" },
  { label: "Phone", node: <ObfuscatedPhone /> },
  { label: "Location", value: "Colorado, USA" },
];

export function Contact() {
  return (
    <section
      id="contact"
      className="section-anchor mx-auto max-w-7xl px-4 pt-8 pb-24 sm:px-6 sm:pt-12 sm:pb-32"
    >
      <SectionLabel>Contact</SectionLabel>
      <h2 className="mt-4 max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
        Talk to a person.
      </h2>
      <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-300">
        Quotes, technical questions, and routine project status all go to the same inbox.<br />No sales-vs-engineering hand-off.
      </p>

      <div className="mt-12 grid grid-cols-1 overflow-hidden rounded-xl border border-bg-border sm:grid-cols-3">
        {CONTACTS.map(({ label, value, href, node }) => (
          <div key={label} className="flex flex-col gap-2 bg-bg-card/85 backdrop-blur-[8px] px-6 py-6">
            <span className="text-sm font-medium uppercase tracking-[0.18em] text-slate-400">
              {label}
            </span>
            {node ?? (href ? (
              <a
                href={href}
                className="text-xl font-medium text-white transition-colors hover:text-brand-500"
              >
                {value}
              </a>
            ) : (
              <span className="text-xl font-medium text-white">{value}</span>
            ))}
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center justify-between gap-6">
        <a
          href="#rfq"
          className="inline-flex items-center gap-2 rounded-md bg-brand-500 px-5 py-3 text-sm font-semibold text-bg-base transition-colors hover:bg-brand-400"
        >
          Send a drawing for quote
          <span aria-hidden="true">→</span>
        </a>
        <div className="flex items-center gap-4">
          <a
            href="https://x.com/anyangleco"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AnyAngle on X"
            className="text-slate-400 transition-colors hover:text-white"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              className="h-7 w-7"
            >
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>
          <a
            href="https://www.instagram.com/anyangle.co/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AnyAngle on Instagram"
            className="text-slate-400 transition-colors hover:text-white"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              className="h-7 w-7"
            >
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
            </svg>
          </a>
          <a
            href="https://www.linkedin.com/company/112972303"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="AnyAngle on LinkedIn"
            className="text-slate-400 transition-colors hover:text-white"
          >
            <svg
              viewBox="0 0 24 24"
              fill="currentColor"
              aria-hidden="true"
              className="h-7 w-7"
            >
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        </div>
      </div>

      {/* WHY a visible release label: the release rehearsals (ENV-B.4) need a
          change a visitor can see flip when prod rolls back and forward;
          `/health` and the `pharos-version` meta are the machine's view of the
          same build-time value (vite.config.ts). Muted on purpose: the footer
          of the last section, not a feature. */}
      <p className="mt-16 text-right text-xs tabular-nums text-slate-400">
        v{__PHAROS_VERSION__}
      </p>
    </section>
  );
}
